/* eslint-disable max-lines */
import { Flex, Margin, Separator } from 'components/base/layout-components';
import { SetFiltersSection } from './SetFiltersSection';
import Text from 'components/base/Text';
import { verticalScroller } from 'components/base/scroller';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import _ from 'lodash';
import InfoBox from 'components/InfoBox';
import {
  formatFiltersToDB,
  getCustomFieldsFromFilters,
  getIssueStateInfoText,
  isNewFiltersAddedToWidget,
  SECTIONS_KEYS
} from './EditCustomPmMetricsModal.service';
import { useDispatch, useSelector } from 'react-redux';
import { toggleModal } from 'state/callers';
import { LINE_HEIGHT } from 'common/constants/sizes';
import { JiraStateMenu, StateTransitionMenu } from './JiraStateMenu';
import { RadioboxGroup } from 'components/RadioboxGroup';
import {
  ISSUE_COUNT_METRIC_NAME,
  ISSUE_TRANSITION_METRIC_NAME,
  PM_CALCULATION_STRATEGY,
  CUSTOM_PM_METRICS_TYPES
} from '@linearb/linjs-common';
import { SettingValues } from 'common/services/settings.service';
import {
  BUTTONS_KEYS,
  BUTTONS_TEXTS,
  CUSTOM_FIELDS_KEY,
  INCLUDE_ALL_ISSUES_VALUE,
  ISSUE_INCLUSION_OPTIONS,
  ISSUE_STATES_KEY,
  ISSUE_TYPES_KEY,
  IS_ALL_ISSUE_TYPES_SELECTED_KEY,
  ONLY_INCLUDE_CREATED_VALUE,
  STATE_TRANSITIONS_KEY
} from './EditCustomPmMetricsModal.constants';
import useSettingsService from 'components/hooks/useSettingsService';
import { Header } from './Header';
import { WidgetName } from './WidgetName';
import { Actions } from './Actions';
import { SingleSelectMenuItem } from './SingleSelectMenuItem';
import { getProjectsToAddToPmImportStrategyByBoardsIds } from 'common/services/pm.service';
import useToast from 'components/hooks/useToast';
import { boardsProviderKeysSelector } from 'state/selectors/boards';
import { FILTER_TYPES } from 'common/constants/filters';
import { PM_PROVIDERS } from 'common/constants/constants';
import { pmToolSensorIdSelector } from 'state/selectors/sensors-authorizations';
import { unionEntitiesWithPMImportSettings } from 'components/FilterMenus/FilterMenus.service';
import useEntitiesLimitation from 'views/root/views/home/hooks/useEntitiesLimitation';
import { getTooManyEntitiesSelectedErrorMessage } from 'views/root/views/home/views/settings/views/ProjectManagement/services/project-management.service';

const Scroll = styled.div`
  max-height: 55rem;
  ${verticalScroller()};
`;

const shouldSaveToPmImportStrategy = ({
  pmImportSettings,
  mergedBoards,
  mergedEpics,
  mergedProjects
}) => {
  const { boards, epics, projects } = pmImportSettings;
  return (
    !_.isEqual(epics, mergedEpics) ||
    !_.isEqual(boards, mergedBoards) ||
    !_.isEqual(projects, mergedProjects)
  );
};

const saveToImportSettings = async (
  updateSetting,
  currentFilters,
  getSettingOrDefinitionValue,
  toast,
  allBoardsByProviderIds,
  widgetFilters,
  entitiesDeviation,
  entitiesLimit
) => {
  const pmImportSettings = getSettingOrDefinitionValue(
    {
      name: SettingValues.PM_IMPORT_STRATEGY
    },
    {}
  );

  const { boards = [], epics = [], projects = [] } = pmImportSettings;

  const mergedEpics = unionEntitiesWithPMImportSettings(
    epics,
    currentFilters[FILTER_TYPES.EPIC],
    FILTER_TYPES.EPIC
  );
  const mergedBoards = unionEntitiesWithPMImportSettings(
    boards,
    currentFilters[FILTER_TYPES.BOARD],
    FILTER_TYPES.BOARD
  );

  const boardsIds = _.get(currentFilters, 'board', []).map(({ id }) => id);

  const mergedProjects = getProjectsToAddToPmImportStrategyByBoardsIds(
    boardsIds,
    projects,
    allBoardsByProviderIds
  );
  const newBoardsAdded = isNewFiltersAddedToWidget(
    currentFilters,
    widgetFilters,
    FILTER_TYPES.BOARD
  );

  const deviation = entitiesDeviation(mergedBoards);

  if (newBoardsAdded && deviation > 0) {
    toast({
      message: getTooManyEntitiesSelectedErrorMessage({
        entityName: FILTER_TYPES.BOARD,
        deviation,
        limit: entitiesLimit
      }),
      variant: 'error'
    });
    return;
  }

  if (newBoardsAdded)
    toast({
      message:
        'Boards selection updated. Information of relevant projects will be collected',
      variant: 'success'
    });

  const payload = {
    ...pmImportSettings,
    boards: mergedBoards,
    epics: mergedEpics,
    ...(mergedProjects ? { projects: mergedProjects } : {})
  };
  if (
    shouldSaveToPmImportStrategy({
      pmImportSettings,
      mergedBoards,
      mergedEpics,
      mergedProjects
    })
  ) {
    updateSetting(SettingValues.PM_IMPORT_STRATEGY, payload);
  }
};

const EditCustomPmMetricsModal = ({
  widget,
  setEditableReport,
  editableReport,
  issueTypes = [],
  setEditMode,
  onChange
}) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const { getSettingOrDefinitionValue, updateSetting } = useSettingsService();

  const sensorId = useSelector(pmToolSensorIdSelector);

  const allBoardsByProviderIds = useSelector(boardsProviderKeysSelector);
  const { entitiesDeviation, entitiesLimit } = useEntitiesLimitation(
    FILTER_TYPES.BOARD
  );

  const widgetFilters = _.get(widget, 'filters', {});

  const [currentFilters, setCurrentFilters] = useState(widgetFilters);
  const [stateStrategy, setStateStrategy] = useState(
    _.get(widget, 'metrics[0].name', ISSUE_COUNT_METRIC_NAME)
  );
  const [calculationStrategy, setCalculationStrategy] = useState(
    _.get(widget, 'calculation_strategy', 'count')
  );
  const [includeItems, setIncludeItems] = useState(
    _.get(widget, 'created_only')
      ? ONLY_INCLUDE_CREATED_VALUE
      : INCLUDE_ALL_ISSUES_VALUE
  );

  const [name, setName] = useState(_.get(widget, 'name'));

  useEffect(() => {
    if (
      includeItems === ONLY_INCLUDE_CREATED_VALUE &&
      stateStrategy !== ISSUE_COUNT_METRIC_NAME
    ) {
      setStateStrategy(ISSUE_COUNT_METRIC_NAME);
    }
  }, [includeItems]);

  const isCreatedOnly = includeItems === ONLY_INCLUDE_CREATED_VALUE;
  const isSaveEnabled =
    _.get(currentFilters, ISSUE_TYPES_KEY, []).length &&
    ((_.get(currentFilters, ISSUE_STATES_KEY, []).length &&
      stateStrategy === ISSUE_COUNT_METRIC_NAME) ||
      isCreatedOnly ||
      (_.get(currentFilters, 'state_transitions.to', null) &&
        stateStrategy === ISSUE_TRANSITION_METRIC_NAME));

  const handleSave = async () => {
    const isAllIssueTypesSelected =
      _.get(currentFilters, ISSUE_TYPES_KEY).length === issueTypes?.length;
    const customFields = getCustomFieldsFromFilters(currentFilters);

    const filtersToDB = {
      boards: formatFiltersToDB.boards(currentFilters[FILTER_TYPES.BOARD]),
      epics: formatFiltersToDB.epics(currentFilters[FILTER_TYPES.EPIC]),
      labels: formatFiltersToDB.labels(currentFilters[FILTER_TYPES.LABEL]),
      team_ids: formatFiltersToDB.team_ids(
        currentFilters[FILTER_TYPES.ORGANIZATION_TEAMS]
      ),
      [CUSTOM_FIELDS_KEY]: formatFiltersToDB.field(customFields)
    };
    filtersToDB[ISSUE_TYPES_KEY] = _.get(currentFilters, ISSUE_TYPES_KEY);
    filtersToDB[ISSUE_STATES_KEY] = _.get(currentFilters, ISSUE_STATES_KEY);
    filtersToDB[STATE_TRANSITIONS_KEY] = _.get(
      currentFilters,
      STATE_TRANSITIONS_KEY
    );
    filtersToDB[IS_ALL_ISSUE_TYPES_SELECTED_KEY] = isAllIssueTypesSelected;

    await saveToImportSettings(
      updateSetting,
      currentFilters,
      getSettingOrDefinitionValue,
      toast,
      allBoardsByProviderIds,
      widgetFilters,
      entitiesDeviation,
      entitiesLimit
    );

    if (onChange) {
      onChange(
        {
          ...widget,
          name,
          filters: filtersToDB,
          metrics: [{ name: stateStrategy }],
          calculation_strategy: calculationStrategy,
          created_only: isCreatedOnly
        },
        false
      );
    } else {
      const widgetIndex = editableReport?.definition?.widgets?.findIndex(
        currentWidget => currentWidget.id === widget.id
      );
      editableReport.definition.widgets[widgetIndex] = {
        ...editableReport.definition.widgets[widgetIndex],
        name,
        filters: filtersToDB,
        metrics: [{ name: stateStrategy }],
        calculation_strategy: calculationStrategy,
        created_only: isCreatedOnly
      };
      setEditableReport({ ...editableReport });
      setEditMode(true);
    }
    toggleModal()(dispatch);
  };

  const infoText = getIssueStateInfoText(currentFilters);

  return (
    <Flex direction='column' padding='medium' pb='small' height='100%'>
      <Header />
      <Separator direction='horizontal' width='100%' height='1px' />
      <Flex direction='column' pl='small' pt='small'>
        <Scroll>
          <WidgetName name={name} setName={setName} />
          <RadioboxGroup
            title='select metric type'
            description='Based on the applied filters below for a set of selected issues,
            please select between 2 metric types: counting issues, or the total
            amount of story points.'
            value={calculationStrategy}
            setValue={setCalculationStrategy}
            options={Object.values(PM_CALCULATION_STRATEGY)}
          />
          <RadioboxGroup
            description='Select if you would like to see all items, or only items that were created for the selected time frame.'
            value={includeItems}
            setValue={setIncludeItems}
            options={ISSUE_INCLUSION_OPTIONS}
          />
          <SetFiltersSection
            title='SCOPE'
            description='Filter by a team, board, epic, label and/or field'
            buttonMinWidth='10rem'
            selectedItems={currentFilters}
            setSelectedItems={setCurrentFilters}
          />

          <SingleSelectMenuItem
            isMultiple={true}
            isRequired
            titleKey='name'
            title={SECTIONS_KEYS[sensorId].ISSUE_TYPE}
            buttonText={BUTTONS_TEXTS[sensorId][BUTTONS_KEYS.ISSUE_TYPES]}
            itemsList={issueTypes}
            selectedItems={_.get(currentFilters, 'issue_types', [])}
            setSelectedItems={values =>
              setCurrentFilters({ ...currentFilters, issue_types: values })
            }
            showClearAll={true}
          />
          <RadioboxGroup
            disabled={isCreatedOnly}
            title={SECTIONS_KEYS[sensorId].ISSUE_STATE}
            description={`${
              sensorId === PM_PROVIDERS.JIRA ? 'Jira' : 'Azure'
            } state: all issues that were in this state at any point during the selected time period. Transition state: all issues that transitioned between two specific states during this time period`}
            value={stateStrategy}
            setValue={setStateStrategy}
            isRequired={!isCreatedOnly}
            options={Object.values(CUSTOM_PM_METRICS_TYPES)}
          />
          {stateStrategy === ISSUE_COUNT_METRIC_NAME && (
            <JiraStateMenu
              isMultiple={true}
              itemKey={'name'}
              titleKey='name'
              title={SECTIONS_KEYS[sensorId].ISSUE_STATE}
              buttonText={BUTTONS_TEXTS[sensorId][BUTTONS_KEYS.PROVIDER_STATES]}
              description='Please select one issue state'
              selectedItems={_.get(currentFilters, 'issue_states')}
              selectedTransition={_.get(
                currentFilters,
                'state_transitions',
                []
              )}
              setSelectedItems={values =>
                setCurrentFilters({
                  ...currentFilters,
                  issue_states: values,
                  state_transitions: null
                })
              }
              width='22rem'
            />
          )}
          {stateStrategy === ISSUE_TRANSITION_METRIC_NAME && (
            <StateTransitionMenu
              buttonText={
                BUTTONS_TEXTS[sensorId][BUTTONS_KEYS.STATE_TRANSITION]
              }
              titleKey='name'
              title='JIRA STATE'
              selectedItems={_.get(currentFilters, 'state_transitions', [])}
              setSelectedItems={values =>
                setCurrentFilters({
                  ...currentFilters,
                  state_transitions: values,
                  issue_states: []
                })
              }
            />
          )}
          {Boolean(infoText) && stateStrategy === ISSUE_COUNT_METRIC_NAME && (
            <Margin r='small'>
              <InfoBox
                mt='none'
                mb='small'
                iconAlign='center'
                iconSize='medium'
              >
                <Text
                  size='medium'
                  color='shuttleGray'
                  lineHeight={LINE_HEIGHT.medium}
                >
                  {infoText}
                </Text>
              </InfoBox>
            </Margin>
          )}
        </Scroll>
      </Flex>
      <Separator direction='horizontal' width='100%' height='1px' />
      <Actions handleSave={handleSave} isValid={isSaveEnabled} />
    </Flex>
  );
};

export default EditCustomPmMetricsModal;
